<template>
	<q-page padding>
		<div class="q-display-1 q-mb-md text-secondary">
			<q-icon name="far fa-books" class="q-mb-sm" /> {{ $t(title_library) }}
		</div>

		<div class="row gutter-sm">
			<div class="col-xs-12 col-sm-12">
				<q-field :count="256">
					<q-input
						v-model="title"
						ref="title"
						:float-label="$t('title')"
						:error="$v.title.$error"
						@input="$v.title.$touch"
					/>
				</q-field>
			</div>

			<div class="col-xs-12 col-sm-12">
				<p class="caption">{{ $t('introduction') }}</p>

				<q-editor
					v-model="description"
					ref="description"
					:error="$v.description.$error"
					@input="$v.description.$touch"
					:toolbar="[
						['bold', 'italic', 'strike', 'underline'],
						['left', 'center', 'right', 'justify'],
						['link', 'upload'],
						[
							{
								label: null,
								icon: 'fas fa-font',
								fixedLabel: true,
								fixedIcon: true,
								list: 'no-icons',
								options: ['p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'code'],
							},
							{
								label: null,
								icon: 'fas fa-text-height',
								fixedLabel: true,
								fixedIcon: true,
								list: 'no-icons',
								options: [
									'size-1',
									'size-2',
									'size-3',
									'size-4',
									'size-5',
									'size-6',
									'size-7',
								],
							},
							'removeFormat',
						],
						['quote', 'unordered', 'ordered'],
						['custom_btn'],
					]"
					:definitions="{
						bold: { label: null, icon: 'fas fa-bold' },
						italic: { label: null, icon: 'fas fa-italic' },
						strike: { label: null, icon: 'fas fa-strikethrough' },
						underline: { label: null, icon: 'fas fa-underline' },
						left: { label: null, icon: 'fas fa-align-left' },
						center: { label: null, icon: 'fas fa-align-center' },
						right: { label: null, icon: 'fas fa-align-right' },
						justify: { label: null, icon: 'fas fa-align-justify' },
						link: { label: null, icon: 'fas fa-link' },
						removeFormat: { label: null, icon: 'fas fa-eraser' },
						quote: { label: null, icon: 'fas fa-quote-right' },
						unordered: { label: null, icon: 'fas fa-list-ul' },
						ordered: { label: null, icon: 'fas fa-list-ol' },
					}"
				>
					<q-btn
						slot="custom_btn"
						dense
						flat
						color="black"
						icon="fal fa-code"
						label="HTML"
						@click="show_html = true"
					/>
				</q-editor>
			</div>

			<div class="col-xs-12 col-sm-12">
				<q-select
					v-model="category"
					ref="category"
					filter
					autofocus-filter
					:float-label="$t('categories')"
					:options="optionCategories"
					:error="$v.category.$error"
					@input="$v.category.$touch"
				/>
			</div>

			<div class="col-xs-12 col-sm-12">
				<q-select
					v-model="tag"
					:options="optionTags"
					:float-label="$t('tags')"
					multiple
					chips
					filter
					autofocus-filter
				/>
			</div>

			<div class="col-12">
				<q-select
					v-model="timezone"
					ref="timezone"
					:options="timezones"
					:float-label="$t('timezone')"
					filter
					:error="$v.timezone.$error"
					@input="$v.timezone.$touch"
				/>
			</div>
			<div class="col-xs-12 col-sm-6">
				<q-datetime
					v-model="start_date"
					ref="start_date"
					type="datetime"
					:float-label="$t('publish_date')"
					format="DD/MM/YYYY HH:mm"
					clearable
					:error="$v.start_date.$error"
					@input="$v.start_date.$touch"
				/>
			</div>

			<div class="col-xs-12 col-sm-6">
				<q-datetime
					v-model="unpublish_date"
					ref="unpublish_date"
					type="datetime"
					:float-label="$t('unpublish_date')"
					format="DD/MM/YYYY HH:mm"
					clearable
				/>
			</div>
		</div>

		<q-tabs v-model="tab" color="secondary" class="shadow-1 q-mt-lg">
			<q-tab
				slot="title"
				name="permissions"
				icon="fal fa-clipboard-list-check"
				:label="$t('permissions')"
			/>

			<q-tab-pane name="permissions" class="q-px-lg q-pb-xl">
				<div class="row gutter-sm">
					<div class="col-xs-12 col-sm-12">
						<p class="caption">{{ $t('areas') }}</p>
						<q-checkbox
							class="q-mb-sm"
							v-model="all_areas"
							:val="true"
							:label="$t('select_all_areas')"
							@input="checkAllAreas()"
						/>
						<select-multiple
							label=""
							:filters="area"
							:options="areas"
							@on_value="area = $event"
							:error="$v.area.$error"
							:input="$v.area.$touch"
						/>
					</div>

					<div class="col-xs-12 col-sm-12">
						<p class="caption">{{ $t('profiles') }}</p>

						<div class="row gutter-sm">
							<div class="col-xs-12 col-sm-12">
								<q-checkbox
									v-model="all_profiles"
									:val="true"
									:label="$t('all')"
									@input="checkAll()"
								/>
							</div>

							<div
								class="col-xs-12 col-sm-4"
								v-for="(item, index) in profiles"
								:key="index"
								:class="getErrorClass($v.profile.$error)"
							>
								<q-checkbox
									keep-color
									v-model="profile"
									ref="profile"
									:val="item.value"
									:label="item.label"
									@input="$v.profile.$touch"
									:color="getErrorColor($v.profile.$error)"
								/>
							</div>

							<div class="col-xs-12 col-sm-12">
								<q-select
									v-model="user_group"
									ref="user_group"
									:options="user_groups"
									:float-label="$t('groups')"
									:error="$v.user_group.$error"
									@input="$v.user_group.$touch"
									multiple
									chips
								/>
							</div>
						</div>
					</div>
				</div>
			</q-tab-pane>
		</q-tabs>

		<div class="row gutter-sm q-mt-sm">
			<div class="col-12">
				<div class="row gutter-sm">
					<div class="col-xs-12 col-sm-6">
						<p class="caption">{{ $t('image') }}</p>
						<uploader
							content_type="images"
							@fileUploaded="imageUploaded"
							:aspect_ratio="52 / 29"
							:aspect_ratio_string="'52/29'"
							:multiple="false"
							:cropper="true"
						/>

						<p v-if="image_url" class="q-mt-md file-url">
							<q-icon name="fal fa-check" color="positive" />
							<a :href="image_url" target="_blank"> {{ $truncateLink(image_url) }}</a>
						</p>
					</div>

					<div class="col-xs-12 col-sm-6">
						<p class="caption">{{ $t('file') }}</p>
						<uploader
							content_type=""
							ref="file_url"
							@fileUploaded="fileUploaded"
							:multiple="false"
							:error="$v.file_url.$error"
						/>
						<small class="text-faded">{{ $t('helper_uploader_ppt_library') }}</small>
						<p v-if="file_url" class="q-mt-md file-url">
							<q-icon name="fal fa-check" color="positive" />
							<a :href="file_url_complete" target="_blank">
								{{ $truncateLink(file_url) }}</a
							>
						</p>
					</div>
					<div class="col-xs-12 col-sm-6">
						<p class="caption">
							{{ $t('alt_image') }}
							<q-icon
								name="fal fa-question-circle"
								size="1.5rem"
								color="grey"
								class="q-ml-sm q-pb-sm"
							>
								<q-tooltip
									class="custom-tooltip"
									anchor="top left"
									self="bottom right"
								>
									{{ $t('alt_image_information_helper') }}
								</q-tooltip>
							</q-icon>
						</p>
						<div class="flex justify-between">
							<template v-for="lang in languages" class="row flex">
								<q-field :count="170" :key="lang.id" v-if="lang.code === 'pt-br'">
									<q-input
										v-model="language_br"
										:float-label="$t(`${lang.name}`)"
										ref="language_br"
										:error="$v.language_br.$error"
										@input="$v.language_br.$touch"
									/>
								</q-field>
								<q-field :count="170" :key="lang.id" v-if="lang.code === 'en-us'">
									<q-input
										v-model="language_us"
										:float-label="$t(`${lang.name}`)"
										ref="language_us"
										:error="$v.language_us.$error"
										@input="$v.language_us.$touch"
									/>
								</q-field>
								<q-field :count="170" :key="lang.id" v-if="lang.code === 'es-es'">
									<q-input
										v-model="language_es"
										:float-label="$t(`${lang.name}`)"
										ref="language_es"
										:error="$v.language_es.$error"
										@input="$v.language_es.$touch"
									/>
								</q-field>
								<q-field :count="170" :key="lang.id" v-if="lang.code === 'fr-fr'">
									<q-input
										v-model="language_fr"
										:float-label="$t(`${lang.name}`)"
										ref="language_fr"
										:error="$v.language_fr.$error"
										@input="$v.language_fr.$touch"
									/>
								</q-field>
							</template>
						</div>
					</div>
				</div>
			</div>

			<!-- <div class="col-xs-12 col-sm-6">
				<p class="caption">{{ $t('status') }}</p>
				<q-btn-toggle v-model="status" :toggle-color="getColorStatus" :options="statuses" />
			</div> -->

			<div class="col-xs-12 col-sm-6">
				<p class="caption">{{ $t('allow_download') }}</p>
				<q-btn-toggle
					v-model="allow_download"
					:toggle-color="getColorAllowDownload"
					:options="allow_download_options"
				/>
			</div>
		</div>

		<div class="row gutter-sm q-my-sm" v-if="id_file != ''">
			<div class="col-xs-12 col-sm-11">
				<q-input v-model="url_library" :float-label="$t('link')" :disable="true" />
			</div>

			<div class="col-xs-12 col-sm-1 q-mt-lg">
				<q-btn
					dense
					color="primary"
					icon="fal fa-copy"
					class="full-width"
					@click="copyLink()"
				>
					<q-tooltip>
						{{ $t('copy_link') }}
					</q-tooltip>
				</q-btn>
			</div>
		</div>

		<div class="row gutter-sm justify-end q-mt-lg">
			<div class="col-xs-12 col-sm-3" :class="getOrder('cancel')">
				<q-btn
					color="tertiary"
					class="full-width"
					:label="$t('back')"
					icon="far fa-chevron-left"
					@click="$router.push({ name: 'admin_libraries' })"
				/>
			</div>

			<div class="col-xs-12 col-sm-3" :class="getOrder('save')">
				<q-btn
					color="secondary"
					class="full-width"
					:label="$t('save')"
					icon="far fa-save"
					@click="saveData()"
				/>
			</div>
		</div>

		<q-modal
			v-model="show_html"
			:content-css="{ minWidth: getMinWidth, maxWidth: getMaxWidth, minHeight: '80vh' }"
			no-backdrop-dismiss
			no-esc-dismiss
		>
			<q-modal-layout>
				<q-toolbar slot="header">
					<q-btn flat round dense @click="show_html = false" icon="fas fa-times" />
					<q-toolbar-title> HTML </q-toolbar-title>
				</q-toolbar>

				<div class="layout-padding">
					<div class="row">
						<div class="col-12">
							<q-input
								type="textarea"
								rows="10"
								float-label="HTML"
								v-model="description"
							/>
						</div>
					</div>

					<div class="row gutter-sm justify-end q-mt-lg">
						<div class="col-xs-12 col-sm-3">
							<q-btn
								color="tertiary"
								class="full-width"
								:label="$t('close')"
								icon="far fa-ban"
								@click="show_html = false"
							/>
						</div>
					</div>
				</div>
			</q-modal-layout>
		</q-modal>
	</q-page>
</template>

<script>
import Uploader from 'components/Uploader'
import { mapGetters } from 'vuex'
import { date } from 'quasar'
import { required, requiredIf, maxLength } from 'vuelidate/lib/validators'
import * as moment from 'moment'
import * as tz from 'moment-timezone'
import SelectMultiple from 'components/utils/SelectMultiple'
export default {
	i18nOptions: { namespaces: ['custom', 'common', 'library', 'admin'] },

	data() {
		return {
			language_br: '',
			language_us: '',
			language_es: '',
			language_fr: '',
			id_file: '',
			title: '',
			description: '',
			category: '',
			tag: [],
			start_date: '',
			unpublish_date: '',
			timezone: '',
			image_url: '',
			file_url: '',
			file_url_complete: '',
			status: 1,
			// statuses: [
			// 	{ label: this.$t('published'), value: 1 },
			// 	{ label: this.$t('unpublished'), value: 2 },
			// ],
			allow_download: true,
			allow_download_options: [
				{ label: this.$t('yes'), value: true },
				{ label: this.$t('no'), value: false },
			],
			all_profiles: false,
			tab: 'permissions',
			area: [],
			profile: [],
			user_group: [],
			show_html: false,
			all_areas: false,
		}
	},

	components: {
		uploader: Uploader,
		'select-multiple': SelectMultiple,
	},

	computed: {
		...mapGetters({
			access_token: 'common/access_token',
			categories: 'registration_lists/library_file_categories',
			profiles: 'registration_lists/profiles',
			areas: 'registration_lists/country_areas',
			user_groups: 'registration_lists/group_users',
			tags: 'registration_lists/tags',
			clients: 'params/clients',
			id_user_client_selected: 'common/id_user_client_selected',
			timezones: 'registration_lists/timezones',
			languages: 'params/languages',
		}),

		getHeader() {
			let headers = {}
			return (headers = {
				headers: {
					'content-type': 'application/json',
					authorization:
						'Basic ' +
						btoa(process.env.APP_CLIENT_ID + ':' + process.env.APP_CLIENT_SECRET),
					authorization: 'Bearer ' + this.access_token,
				},
			})
		},

		getMaxWidth() {
			if (this.$q.platform.is.mobile) return '100vw'
			else return '80vw'
		},

		getMinWidth() {
			if (!this.$q.platform.is.mobile) return '90vw'
		},

		filteredTags() {
			let category = this.categories.find((obj) => {
				return obj.value === this.category
			})
			if (category) {
				return this.tags.filter((obj) => {
					return category.areas.includes(obj.area)
				})
			}
			return []
		},

		// getColorStatus() {
		// 	if (this.status == 1) return 'positive'
		// 	else return 'negative'
		// },

		getColorAllowDownload() {
			if (this.allow_download) return 'positive'
			else return 'negative'
		},

		title_library() {
			if (this.id_user_client_selected) {
				let obj = this.clients.find((obj) => {
					return obj.id === this.id_user_client_selected
				})
				if (obj) {
					return obj.title_library == '' ? 'library' : obj.title_library
				}
			}
		},

		optionCategories() {
			if (this.categories.length > 0) {
				return this.categories.map((c) => {
					return {
						value: c.value,
						label: c.label,
					}
				})
			} else return []
		},

		optionTags() {
			if (this.tags.length > 0) {
				return this.tags.map((t) => {
					let areas = t.areas.map((a) => a.name)
					return {
						value: t.value,
						label: t.label,
						sublabel: areas.join(', '),
					}
				})
			} else return []
		},

		url_library: {
			get() {
				let url = process.env.SITE_URL
				return `${url}/libraries/${this.id_file}`
			},
			set() {
				''
			},
		},
	},

	methods: {
		fetchData() {
			this.$q.loading.show()

			this.$axios
				.get(`/admin_library_files/files/${this.id_file}/`, this.getHeader)
				.then((response) => {
					this.title = response.data.title
					this.description = response.data.description
					this.file_url = response.data.file_url
					this.file_url_complete = response.data.file_url_complete
					this.status = response.data.status
					this.profile = response.data.profiles
						? response.data.profiles.map((p) => p.id)
						: []
					this.user_group = response.data.user_groups
						? response.data.user_groups.map((u) => u.id)
						: []
					this.category = response.data.library_file_category
					this.image_url = response.data.image_url
					this.allow_download = response.data.allow_download
					this.tag = response.data.tags ? response.data.tags.map((t) => t.id) : []
					this.area = response.data.areas
						? response.data.areas.map((a) => {
								return this.areas.find((e) => a.area_id == e.area_id).value
						  })
						: []
					const altTextMap = {}
					response.data.library_image_alt_texts.forEach((altTextObj) => {
						altTextMap[altTextObj.language] = altTextObj.alt_text
					})
					this.language_br = altTextMap['pt-br'] || ''
					this.language_us = altTextMap['en-us'] || ''
					this.language_es = altTextMap['es-es'] || ''
					this.language_fr = altTextMap['fr-fr'] || ''
					if (response.data.timezone) this.timezone = response.data.timezone
					if (response.data.publish_date) this.start_date = response.data.publish_date
					if (response.data.unpublish_date)
						this.unpublish_date = response.data.unpublish_date
					this.$q.loading.hide()
				})
				.catch((e) => {
					this.$q.loading.hide()
					console.log(e)
				})
		},

		saveData() {
			this.$v.$touch()

			if (!this.$v.$error) {
				this.$q.loading.show()

				let data = {
					library_file_category: this.category,
					description: this.$clearEmoticons(this.description),
					file_url: this.file_url,
					image_url: this.image_url,
					status: this.status,
					title: this.title,
					allow_download: this.allow_download,
					library_image_alt_texts: {
						'pt-br': this.language_br ? this.language_br : '',
						'en-us': this.language_us ? this.language_us : '',
						'es-es': this.language_es ? this.language_es : '',
						'fr-fr': this.language_fr ? this.language_fr : '',
					},
				}
				if (this.profile.length) data.profiles = this.profile
				if (this.user_group.length) data.user_groups = this.user_group
				if (this.tag.length) data.tags = this.tag
				if (this.area.length) data.areas = this.area

				this.timezone ? (data.timezone = this.timezone) : (data.timezone = null)
				this.start_date ? data.publish_date : (data.publish_date = null)
				this.status == 1 && this.unpublish_date
					? data.unpublish_date
					: (data.unpublish_date = null)
				this.start_date
					? (data.publish_date = moment(
							date.formatDate(this.start_date, 'YYYY-MM-DDTHH:mm')
					  )
							.tz(this.timezone, true)
							.format('YYYY-MM-DDTHH:mm:ssZ'))
					: (data.publish_date = null)

				this.unpublish_date
					? (data.unpublish_date = moment(
							date.formatDate(this.unpublish_date, 'YYYY-MM-DDTHH:mm')
					  )
							.tz(this.timezone, true)
							.format('YYYY-MM-DDTHH:mm:ssZ'))
					: (data.unpublish_date = null)

				if (this.id_file != 0) {
					this.$axios
						.put(`/admin_library_files/files/${this.id_file}/`, data, this.getHeader)
						.then(() => {
							this.$q.loading.hide()
							this.$router.push({ name: 'admin_libraries' })
						})
						.catch((e) => {
							this.$q.loading.hide()
							console.log(e)
							this.$q.notify({
								message: this.$t('basic_data_save_error'),
								type: 'negative',
								icon: 'fal fa-exclamation-triangle',
								position: 'bottom',
							})
						})
				} else {
					this.$axios
						.post(`/admin_library_files/files/`, data, this.getHeader)
						.then(() => {
							this.$q.loading.hide()
							this.$router.push({ name: 'admin_libraries' })
						})
						.catch((e) => {
							this.$q.loading.hide()
							console.log(e)
							this.$q.notify({
								message: this.$t('basic_data_save_error'),
								type: 'negative',
								icon: 'fal fa-exclamation-triangle',
								position: 'bottom',
							})
						})
				}
			} else {
				this.$formInputErrorFocus(this.$v, this.$refs)
				this.$q.notify({
					message: this.$t('make_sure_that_all_fields_are_filled_in'),
					type: 'negative',
					icon: 'fal fa-exclamation-triangle',
					position: 'top',
				})
			}
		},

		imageUploaded(url, fileUrlSigned) {
			this.image_url = url
		},

		fileUploaded(url) {
			this.file_url = url
			this.file_url_complete = url
		},

		getOrder(name) {
			if (name == 'cancel') {
				if (this.$q.platform.is.mobile) return 'order-last'
				else return 'order-first'
			} else if (name == 'save') {
				if (this.$q.platform.is.mobile) return 'order-first'
				else return 'order-last'
			}
		},

		checkAll() {
			if (this.all_profiles) {
				this.profiles.forEach((element) => {
					this.profile.push(element.value)
				})
			} else {
				this.profile = []
			}
		},

		getErrorColor(state) {
			if (state) return 'negative'
			else return ''
		},

		getErrorClass(state) {
			if (state) return 'checkbox-error'
			else return ''
		},

		copyLink() {
			this.$copyText(this.url_library).then(
				(e) => {
					this.$q.notify({
						message: this.$t('link_copied_to_clipboard'),
						type: 'positive',
						icon: 'fal fa-copy',
					})
				},
				(e) => {
					console.log(e)
				}
			)
		},

		fetchTimezones() {
			let values = []
			moment.tz
				.names()
				.reduce((memo, tz) => {
					memo.push({
						name: tz,
						offset: moment.tz(tz).utcOffset(),
					})
					return memo
				}, [])
				.sort((a, b) => {
					return a.offset - b.offset
				})
				.reduce((memo, tz) => {
					const timezone = tz.offset ? moment.tz(tz.name).format('Z') : ''
					values.push({ value: tz.name, label: `(GMT${timezone}) ${tz.name}`, ...tz })
				}, '')
			this.$store.dispatch('registration_lists/SET_TIMEZONES', [values])
		},

		checkAllAreas() {
			if (this.all_areas) {
				this.area = this.areas.map((e) => e.value)
			} else {
				this.area = []
			}
		},
	},

	created() {
		this.fetchTimezones()
		this.id_file = this.$route.params.id
		if (this.id_file != 0) this.fetchData()
	},

	watch: {
		$route: function(refreshPage) {
			this.id_file = this.$route.params.id
			if (this.id_file != 0) this.fetchData()
		},
	},

	validations() {
		return {
			title: { required, maxLength: maxLength(256) },
			language_br: { maxLength: maxLength(256) },
			language_us: { maxLength: maxLength(256) },
			language_es: { maxLength: maxLength(256) },
			language_fr: { maxLength: maxLength(256) },
			description: { required },
			profiles: { required },
			category: { required },
			file_url: { required },
			area: {
				requiredIf: requiredIf(() => {
					if (this.profile.length == 0 && this.user_group.length == 0) {
						return true
					} else {
						return false
					}
				}),
			},
			profile: {
				requiredIf: requiredIf(() => {
					if (this.profile.length == 0) {
						if (this.profile.length == 0 && this.user_group.length == 0) return true
						else return false
					} else {
						return true
					}
				}),
			},
			timezone: {
				requiredIf: requiredIf(() => {
					if (this.start_date || this.unpublish_date) return true
					else return false
				}),
			},
			start_date: { required },
			user_group: {
				requiredIf: requiredIf(() => {
					if (this.profile.length == 0 && this.user_group.length == 0) {
						return true
					} else {
						return false
					}
				}),
			},
		}
	},
}
</script>

<style lang="stylus" scoped>
@import '~variables'

.file-url
    a
        text-decoration none
        color black
</style>
